import { useMemo, useState } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import * as W from '../lib/wellness.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useUserData } from '../lib/useUserData.js'
import { useToast } from '../context/ToastContext.jsx'
import { todayKey } from '../lib/dates.js'
import Seo from '../components/Seo.jsx'

const num = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : NaN
}

/* ============================================================
   Shared UI pieces
   ============================================================ */
function UnitToggle({ unit, setUnit }) {
  return (
    <div className="seg" style={{ marginBottom: 18 }}>
      <button type="button" className={unit === 'metric' ? 'active' : ''} onClick={() => setUnit('metric')}>Metric</button>
      <button type="button" className={unit === 'imperial' ? 'active' : ''} onClick={() => setUnit('imperial')}>Imperial</button>
    </div>
  )
}

function SexToggle({ sex, setSex }) {
  return (
    <div className="field">
      <label className="label">Sex <span className="hint">(for the formula)</span></label>
      <div className="seg" style={{ width: '100%' }}>
        <button type="button" style={{ flex: 1 }} className={sex === 'male' ? 'active' : ''} onClick={() => setSex('male')}>Male</button>
        <button type="button" style={{ flex: 1 }} className={sex === 'female' ? 'active' : ''} onClick={() => setSex('female')}>Female</button>
      </div>
    </div>
  )
}

function Num({ label, hint, value, onChange, addon, placeholder, step = 'any' }) {
  return (
    <div className="field">
      <label className="label">{label} {hint && <span className="hint">({hint})</span>}</label>
      {addon ? (
        <div className="input-group">
          <input className="input" type="number" min="0" step={step} inputMode="decimal"
            value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
          <span className="addon">{addon}</span>
        </div>
      ) : (
        <input className="input" type="number" min="0" step={step} inputMode="decimal"
          value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  )
}

function WeightRow({ unit, value, onChange }) {
  return <Num label="Weight" value={value} onChange={onChange} addon={unit === 'metric' ? 'kg' : 'lb'} placeholder={unit === 'metric' ? '70' : '154'} />
}

function HeightRow({ unit, cm, setCm, ft, setFt, inch, setInch }) {
  if (unit === 'metric') {
    return <Num label="Height" value={cm} onChange={setCm} addon="cm" placeholder="175" />
  }
  return (
    <div className="field">
      <label className="label">Height</label>
      <div className="row" style={{ gap: 10 }}>
        <div className="input-group" style={{ flex: 1 }}>
          <input className="input" type="number" min="0" step="any" inputMode="decimal" value={ft} onChange={(e) => setFt(e.target.value)} placeholder="5" />
          <span className="addon">ft</span>
        </div>
        <div className="input-group" style={{ flex: 1 }}>
          <input className="input" type="number" min="0" step="any" inputMode="decimal" value={inch} onChange={(e) => setInch(e.target.value)} placeholder="9" />
          <span className="addon">in</span>
        </div>
      </div>
    </div>
  )
}

function Result({ ready, hero, unit, category, stats = [], note, prompt, save }) {
  const { user } = useAuth()
  const { addWellnessLog } = useUserData()
  const toast = useToast()
  const [saved, setSaved] = useState(false)

  const doSave = async () => {
    const res = await addWellnessLog({ type: save.type, label: save.label, value: save.value, unit: save.unit, date: todayKey() })
    if (res?.error) return toast(res.error, 'red')
    setSaved(true)
    toast('Saved to your progress', 'green')
  }

  return (
    <div className="card result-card">
      {ready ? (
        <div className="fade-in">
          <div className="result-hero">
            <div className="result-hero-value">{hero}<span className="unit"> {unit}</span></div>
            {category && <div style={{ marginTop: 8 }}><span className={`pill ${category.tone}`} style={{ fontSize: '.82rem' }}>{category.label}</span></div>}
          </div>
          {stats.length > 0 && (
            <div className="result-grid">
              {stats.map((s, i) => (
                <div className="stat" key={i}>
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value">{s.value}{s.unit && <span className="stat-unit"> {s.unit}</span>}</div>
                </div>
              ))}
            </div>
          )}
          {note && <div className="warn" style={{ background: 'var(--surface-2)', color: 'var(--text-soft)', borderColor: 'var(--border)' }}>{note}</div>}
          {save && (
            <div style={{ padding: '14px 18px 0' }}>
              {user ? (
                <button className="btn btn-soft btn-block btn-sm" onClick={doSave} disabled={saved}>
                  {saved ? '✓ Saved to progress' : '＋ Save to my progress'}
                </button>
              ) : (
                <Link to="/login" className="btn btn-ghost btn-block btn-sm">Log in to track this over time</Link>
              )}
            </div>
          )}
          <div style={{ padding: '14px 18px' }}>
            <p className="muted small mb-0">Estimate for general wellness — not a diagnosis.</p>
          </div>
        </div>
      ) : (
        <div className="result-empty">
          <p className="muted center" style={{ maxWidth: 300, margin: '0 auto' }}>{prompt}</p>
        </div>
      )}
    </div>
  )
}

// convert a metric/imperial body state to base units
function useBody(defaults = {}) {
  const [unit, setUnit] = useState('metric')
  const [weight, setWeight] = useState('')
  const [cm, setCm] = useState('')
  const [ft, setFt] = useState('')
  const [inch, setInch] = useState('')
  const kg = unit === 'metric' ? num(weight) : W.kgFromLb(num(weight))
  const heightCm = unit === 'metric' ? num(cm) : W.cmFromFtIn(num(ft) || 0, num(inch) || 0)
  return { unit, setUnit, weight, setWeight, cm, setCm, ft, setFt, inch, setInch, kg, heightCm, ...defaults }
}

/* ============================================================
   BMI
   ============================================================ */
function Bmi() {
  const b = useBody()
  const value = W.bmi(b.kg, b.heightCm)
  const cat = W.bmiCategory(value)
  const range = W.healthyWeightRange(b.heightCm)
  const ready = value != null
  const rangeText = range
    ? b.unit === 'metric'
      ? `${W.round(range[0])}–${W.round(range[1])} kg`
      : `${W.round(W.lbFromKg(range[0]))}–${W.round(W.lbFromKg(range[1]))} lb`
    : '—'
  return (
    <div className="grid-calc">
      <div className="card card-pad">
        <UnitToggle unit={b.unit} setUnit={b.setUnit} />
        <WeightRow unit={b.unit} value={b.weight} onChange={b.setWeight} />
        <HeightRow unit={b.unit} cm={b.cm} setCm={b.setCm} ft={b.ft} setFt={b.setFt} inch={b.inch} setInch={b.setInch} />
      </div>
      <Result
        ready={ready} hero={value != null ? W.round(value) : ''} unit="BMI" category={cat}
        stats={[{ label: 'Healthy weight', value: rangeText }]}
        note="BMI is a screening estimate and doesn’t distinguish muscle from fat."
        prompt="Enter your weight and height to see your BMI."
        save={ready ? { type: 'bmi', label: 'BMI', value: W.round(value), unit: '' } : null}
      />
    </div>
  )
}

/* ============================================================
   BMR
   ============================================================ */
function Bmr() {
  const b = useBody()
  const [sex, setSex] = useState('male')
  const [age, setAge] = useState('')
  const value = W.bmr({ sex, kg: b.kg, cm: b.heightCm, age: num(age) })
  const ready = value != null
  return (
    <div className="grid-calc">
      <div className="card card-pad">
        <UnitToggle unit={b.unit} setUnit={b.setUnit} />
        <SexToggle sex={sex} setSex={setSex} />
        <Num label="Age" value={age} onChange={setAge} addon="yrs" placeholder="30" />
        <WeightRow unit={b.unit} value={b.weight} onChange={b.setWeight} />
        <HeightRow unit={b.unit} cm={b.cm} setCm={b.setCm} ft={b.ft} setFt={b.setFt} inch={b.inch} setInch={b.setInch} />
      </div>
      <Result
        ready={ready} hero={value != null ? W.round(value, 0) : ''} unit="kcal/day"
        note="BMR is the energy your body uses at complete rest (Mifflin-St Jeor equation)."
        prompt="Enter your details to estimate your resting metabolic rate."
        save={ready ? { type: 'bmr', label: 'BMR', value: W.round(value, 0), unit: ' kcal' } : null}
      />
    </div>
  )
}

/* ============================================================
   TDEE
   ============================================================ */
function Tdee() {
  const b = useBody()
  const [sex, setSex] = useState('male')
  const [age, setAge] = useState('')
  const [activity, setActivity] = useState('moderate')
  const bmrValue = W.bmr({ sex, kg: b.kg, cm: b.heightCm, age: num(age) })
  const factor = W.ACTIVITY_LEVELS.find((a) => a.id === activity)?.factor
  const value = W.tdee(bmrValue, factor)
  const ready = value != null
  return (
    <div className="grid-calc">
      <div className="card card-pad">
        <UnitToggle unit={b.unit} setUnit={b.setUnit} />
        <SexToggle sex={sex} setSex={setSex} />
        <Num label="Age" value={age} onChange={setAge} addon="yrs" placeholder="30" />
        <WeightRow unit={b.unit} value={b.weight} onChange={b.setWeight} />
        <HeightRow unit={b.unit} cm={b.cm} setCm={b.setCm} ft={b.ft} setFt={b.setFt} inch={b.inch} setInch={b.setInch} />
        <div className="field mb-0">
          <label className="label">Activity level</label>
          <select className="select" value={activity} onChange={(e) => setActivity(e.target.value)}>
            {W.ACTIVITY_LEVELS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>
      </div>
      <Result
        ready={ready} hero={value != null ? W.round(value, 0) : ''} unit="kcal/day"
        stats={ready ? [
          { label: 'Lose weight', value: W.round(value - 500, 0), unit: 'kcal' },
          { label: 'Gain weight', value: W.round(value + 500, 0), unit: 'kcal' },
        ] : []}
        note="Maintenance calories. A ~500 kcal daily deficit/surplus ≈ 0.45 kg (1 lb) per week."
        prompt="Enter your details and activity level to estimate daily calories."
        save={ready ? { type: 'tdee', label: 'TDEE', value: W.round(value, 0), unit: ' kcal' } : null}
      />
    </div>
  )
}

/* ============================================================
   Body fat %
   ============================================================ */
function BodyFat() {
  const [unit, setUnit] = useState('metric')
  const [sex, setSex] = useState('male')
  const [cm, setCm] = useState(''); const [ft, setFt] = useState(''); const [inch, setInch] = useState('')
  const [neck, setNeck] = useState(''); const [waist, setWaist] = useState(''); const [hip, setHip] = useState('')

  const toCm = (v) => (unit === 'metric' ? num(v) : W.cmFromIn(num(v)))
  const heightCm = unit === 'metric' ? num(cm) : W.cmFromFtIn(num(ft) || 0, num(inch) || 0)
  const value = W.bodyFatNavy({ sex, cm: heightCm, neckCm: toCm(neck), waistCm: toCm(waist), hipCm: toCm(hip) })
  const cat = W.bodyFatCategory(value, sex)
  const ready = value != null
  const cUnit = unit === 'metric' ? 'cm' : 'in'

  return (
    <div className="grid-calc">
      <div className="card card-pad">
        <UnitToggle unit={unit} setUnit={setUnit} />
        <SexToggle sex={sex} setSex={setSex} />
        <HeightRow unit={unit} cm={cm} setCm={setCm} ft={ft} setFt={setFt} inch={inch} setInch={setInch} />
        <Num label="Neck" hint="around, below the larynx" value={neck} onChange={setNeck} addon={cUnit} placeholder={unit === 'metric' ? '38' : '15'} />
        <Num label="Waist" hint="at the navel" value={waist} onChange={setWaist} addon={cUnit} placeholder={unit === 'metric' ? '85' : '34'} />
        {sex === 'female' && (
          <Num label="Hips" hint="at the widest point" value={hip} onChange={setHip} addon={cUnit} placeholder={unit === 'metric' ? '95' : '37'} />
        )}
      </div>
      <Result
        ready={ready} hero={value != null ? W.round(value) : ''} unit="% body fat" category={cat}
        note="US Navy circumference method — a solid estimate, not as precise as a DEXA scan."
        prompt="Enter your measurements to estimate body-fat percentage."
        save={ready ? { type: 'bodyfat', label: 'Body Fat', value: W.round(value), unit: '%' } : null}
      />
    </div>
  )
}

/* ============================================================
   Calories burned
   ============================================================ */
function CaloriesBurned() {
  const [unit, setUnit] = useState('metric')
  const [weight, setWeight] = useState('')
  const [activity, setActivity] = useState('walking')
  const [minutes, setMinutes] = useState('')
  const kg = unit === 'metric' ? num(weight) : W.kgFromLb(num(weight))
  const met = W.MET_ACTIVITIES.find((a) => a.id === activity)?.met
  const value = W.caloriesBurned({ met, kg, minutes: num(minutes) })
  const ready = value != null
  const perHour = met && kg > 0 ? W.caloriesBurned({ met, kg, minutes: 60 }) : null
  return (
    <div className="grid-calc">
      <div className="card card-pad">
        <UnitToggle unit={unit} setUnit={setUnit} />
        <div className="field">
          <label className="label">Activity</label>
          <select className="select" value={activity} onChange={(e) => setActivity(e.target.value)}>
            {W.MET_ACTIVITIES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>
        <WeightRow unit={unit} value={weight} onChange={setWeight} />
        <Num label="Duration" value={minutes} onChange={setMinutes} addon="min" placeholder="30" />
      </div>
      <Result
        ready={ready} hero={value != null ? W.round(value, 0) : ''} unit="kcal burned"
        stats={perHour != null ? [{ label: 'Per hour', value: W.round(perHour, 0), unit: 'kcal' }] : []}
        note="Based on MET values — actual burn varies with intensity and fitness."
        prompt="Pick an activity, weight, and duration to estimate calories burned."
      />
    </div>
  )
}

/* ============================================================
   Registry + page shell
   ============================================================ */
const CALCS = {
  bmi: {
    title: 'BMI Calculator', sub: 'Body Mass Index from your height and weight, with the healthy range.', C: Bmi,
    about: 'Body Mass Index (BMI) is a quick screening number that compares your weight to your height — weight in kilograms divided by height in metres squared. A BMI of 18.5–24.9 is generally considered the healthy range, 25–29.9 overweight, and 30 or above obese. Because BMI can’t tell muscle from fat, very muscular or very lean people can read misleadingly high or low, so treat it as a general guide rather than a diagnosis. It’s most useful for spotting trends over time and as a starting point for a conversation with a healthcare provider.',
  },
  bmr: {
    title: 'BMR Calculator', sub: 'Basal Metabolic Rate — calories burned at complete rest.', C: Bmr,
    about: 'Basal Metabolic Rate (BMR) is the number of calories your body burns at complete rest just to stay alive — powering your breathing, circulation, brain, and cell repair. It’s the single largest part of your daily energy use, often 60–70% of the total. This calculator uses the Mifflin-St Jeor equation, one of the most accurate formulas for estimating BMR from your age, sex, height, and weight. Multiply your BMR by an activity factor to get your Total Daily Energy Expenditure (TDEE), the figure you actually plan meals around.',
  },
  tdee: {
    title: 'Daily Calorie (TDEE) Calculator', sub: 'Total daily energy expenditure from your activity level.', C: Tdee,
    about: 'Total Daily Energy Expenditure (TDEE) is how many calories you burn across a full day, including all movement and exercise. It’s your BMR multiplied by an activity factor ranging from sedentary to very active. TDEE is the anchor for any nutrition goal: eat below it to lose weight, at it to maintain, and above it to gain. Because self-reported activity level is only an estimate, treat the result as a starting point and adjust up or down based on how your weight actually responds over two to three weeks.',
  },
  bodyfat: {
    title: 'Body Fat % Calculator', sub: 'US Navy circumference method from simple tape measurements.', C: BodyFat,
    about: 'Body-fat percentage tells you how much of your weight is fat versus muscle, bone, and water — often more informative than scale weight alone. This tool uses the US Navy circumference method, which estimates body fat from tape measurements of your neck and waist (plus hips for women) together with your height. It’s a convenient at-home estimate, not a lab-grade reading like a DEXA scan, but it’s consistent enough to track changes over time. Measure at the same time of day and in the same way each time for the most reliable trend.',
  },
  burned: {
    title: 'Calories Burned Calculator', sub: 'Estimated calories burned for an activity and duration.', C: CaloriesBurned,
    about: 'This calculator estimates how many calories an activity burns from its intensity — measured in METs, or metabolic equivalents — along with your body weight and how long you do it. Heavier people, longer sessions, and higher-intensity activities all burn more. The figures are population averages, so your real burn will vary with fitness, effort, technique, and even temperature. Use the numbers to compare activities and plan your week, not as an exact calorie count to eat back gram-for-gram.',
  },
}

export default function WellnessCalc() {
  const { slug } = useParams()
  const entry = CALCS[slug]
  const Comp = useMemo(() => entry?.C, [entry])
  if (!entry) return <Navigate to="/wellness" replace />

  return (
    <main className="page">
      <Seo title={`${entry.title} | CalculateMyPeptides`}
        description={`${entry.sub} A free, instant ${entry.title.replace(/ Calculator$/, '')} — works in metric or imperial units, no signup, with the formula explained.`}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Wellness', path: '/wellness' }, { name: entry.title, path: `/wellness/${slug}` }]} />
      <div className="container">
        <Link to="/wellness" className="pep-back">← Health &amp; Wellness Calculators</Link>
        <div style={{ margin: '0 0 22px' }}>
          <h1 className="section-title">{entry.title}</h1>
          <p className="section-sub mb-0">{entry.sub}</p>
        </div>
        <Comp />
        {entry.about && (
          <section className="pep-section" style={{ maxWidth: '70ch', marginTop: 30 }}>
            <h2 className="pep-h2">About the {entry.title}</h2>
            <p className="soft">{entry.about}</p>
            <p className="muted small" style={{ marginTop: 10 }}>
              Educational estimate only — not medical advice. For personalized guidance, consult a qualified
              healthcare professional.
            </p>
          </section>
        )}
      </div>
    </main>
  )
}
